// src/components/AddKnowledgeModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
    Chip,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createKnowledge, fetchAllClassifiers } from '../api/knowledgeApi';

const AddKnowledgeModal = ({ open, onClose, onSuccess }) => {
    const [knowledgeText, setKnowledgeText] = useState('');
    const [knowledgeSummary, setKnowledgeSummary] = useState('');
    const [classifiers, setClassifiers] = useState([]);
    const [selectedClassifiers, setSelectedClassifiers] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch classifiers when the modal opens
    useEffect(() => {
        if (open) {
            setKnowledgeText('');
            setKnowledgeSummary('');
            setSelectedClassifiers([]);
            setNewTag('');
            setError(null);
            fetchClassifiers();
        }
    }, [open]);

    const fetchClassifiers = async () => {
        try {
            const data = await fetchAllClassifiers();
            setClassifiers(data);
        } catch (err) {
            console.error('Error fetching classifiers:', err);
            setError('Failed to fetch classifiers');
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !selectedClassifiers.some(tag => tag.classText.toLowerCase() === newTag.trim().toLowerCase())) {
            const newClassifier = {
                classText: newTag.trim(),
                userIdentifier: 'string',
                creationDate: new Date().toISOString()
            };
            setSelectedClassifiers([...selectedClassifiers, newClassifier]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setSelectedClassifiers(selectedClassifiers.filter(tag => tag.classText !== tagToRemove.classText));
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddTag();
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            const creationDate = new Date().toISOString();
            const userIdentifier = 'string';
            const userName = 'string';

            const knowledgeData = {
                id: 0,
                knowledgeText,
                knowledgeSummary,
                userIdentifier,
                userName,
                creationDate,
                classifiers: selectedClassifiers.map(cls => ({
                    id: 0,
                    classText: cls.classText,
                    userIdentifier,
                    userName,
                    creationDate
                })),
                keywords: []
            };

            await createKnowledge(knowledgeData);
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
            setError('Failed to create knowledge entry');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="add-knowledge-modal-title"
            aria-describedby="add-knowledge-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 600,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 2,
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography id="add-knowledge-modal-title" variant="h6" component="h2">
                        Add Knowledge
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                
                {/* Knowledge Summary Input */}
                <TextField
                    label="Knowledge Summary"
                    value={knowledgeSummary}
                    onChange={(e) => setKnowledgeSummary(e.target.value)}
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2 }}
                />

                {/* Knowledge Text Input */}
                <TextField
                    label="Knowledge Text"
                    multiline
                    rows={4}
                    value={knowledgeText}
                    onChange={(e) => setKnowledgeText(e.target.value)}
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2 }}
                />

                {/* Custom Tag Input */}
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Add Custom Tags"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={handleKeyPress}
                        variant="outlined"
                        fullWidth
                        placeholder="Type a tag and press Enter"
                        InputProps={{
                            endAdornment: (
                                <Button
                                    onClick={handleAddTag}
                                    disabled={!newTag.trim()}
                                    sx={{ ml: 1 }}
                                >
                                    Add
                                </Button>
                            ),
                        }}
                    />
                </Box>

                {/* Tag Cloud */}
                {selectedClassifiers.length > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2,
                            p: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            backgroundColor: 'background.default',
                        }}
                    >
                        {selectedClassifiers.map((tag) => (
                            <Chip
                                key={tag.classText}
                                label={tag.classText}
                                onDelete={() => handleRemoveTag(tag)}
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </Paper>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button onClick={onClose} sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={loading || !knowledgeText || !knowledgeSummary}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddKnowledgeModal;
